/* Abano Terme Biocosmesi – theme behaviours (hover menus, tabs, cookie banner, wishlist/compare, toolbar) */
(function () {
  'use strict';

  const DESKTOP = window.matchMedia('(min-width: 1200px)');

  /* ---------- Hover-open header menus (desktop) ---------- */
  function initHoverMenus() {
    const bind = (host, details) => {
      if (!details || host.dataset.abanoHover) return;
      host.dataset.abanoHover = '1';
      let timer;
      host.addEventListener('mouseenter', () => {
        if (!DESKTOP.matches) return;
        clearTimeout(timer);
        details.setAttribute('open', '');
        details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
      });
      host.addEventListener('mouseleave', () => {
        if (!DESKTOP.matches) return;
        timer = setTimeout(() => {
          details.removeAttribute('open');
          details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
        }, 120);
      });
    };
    document.querySelectorAll('header-menu').forEach((menu) => bind(menu, menu.querySelector('details')));
    /* third level (Prodotti → Corpo / Viso): flyout on hover of the submenu item */
    document.querySelectorAll('.header__submenu > li > details').forEach((details) => bind(details.parentElement, details));
  }

  /* ---------- Smart sticky header (desktop ≥1200) ----------
     Reference behaviour: once the page is scrolled past the navigation row, only that row
     (80px, no logo) is pinned to the top; it hides on scroll-down and reappears on scroll-up. */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerEl = this.querySelector('.header');
      this.navRow = this.querySelector('.header__nav-row');
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearches = this.querySelectorAll('predictive-search');
      this.setHeaderHeight = this.setHeaderHeight.bind(this);
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.setHeaderHeight();
      DESKTOP.addEventListener('change', () => { this.reset(); this.setHeaderHeight(); });
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);
      window.addEventListener('resize', this.setHeaderHeight);
    }
    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      window.removeEventListener('resize', this.setHeaderHeight);
    }
    setHeaderHeight() {
      if (this.header) document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }
    threshold() {
      // bottom edge of the static header (top bar + logo area + nav row)
      const top = this.headerEl.getBoundingClientRect().top + window.pageYOffset;
      return top + this.headerEl.offsetHeight;
    }
    onScroll() {
      if (!DESKTOP.matches || !this.navRow || !this.headerEl) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if ([...this.predictiveSearches].some((s) => s.isOpen)) return;
      if (scrollTop > this.threshold()) {
        this.headerEl.classList.add('header--nav-fixed');
        if (scrollTop > this.currentScrollTop) {
          if (!this.preventHide) requestAnimationFrame(this.hide.bind(this));
        } else if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => { this.preventReveal = false; }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else {
        requestAnimationFrame(this.reset.bind(this));
      }
      this.currentScrollTop = scrollTop;
    }
    hide() {
      this.headerEl.classList.add('header--nav-hidden');
      this.querySelectorAll('header-menu').forEach((d) => d.close && d.close());
      this.querySelectorAll('details-modal').forEach((m) => m.close && m.close(false));
    }
    reveal() { this.headerEl.classList.remove('header--nav-hidden'); }
    reset() { if (this.headerEl) this.headerEl.classList.remove('header--nav-fixed', 'header--nav-hidden'); }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------- Mobile drawer close button ---------- */
  function initDrawerClose() {
    document.querySelectorAll('[data-abano-drawer-close]').forEach((btn) => {
      if (btn.dataset.abanoBound) return;
      btn.dataset.abanoBound = 'true';
      btn.addEventListener('click', () => {
        const summary = document.querySelector('#Details-menu-drawer-container > summary');
        if (summary) summary.click();
      });
    });
  }

  /* ---------- Mobile drawer: inline accordion submenus + page push ----------
     The reference off-canvas menu expands submenus in place (chevron toggles a nested list) and pushes the
     whole page 300px to the right while open. Dawn's HeaderDrawer still owns open/close of the outer <details>;
     it adds "menu-opening" on open and removes it right away on close (before its 400ms close animation), so
     that class is mirrored onto <body> as "abano-drawer-open" to drive the push transition both ways. */
  function initDrawerAccordion() {
    const container = document.getElementById('Details-menu-drawer-container');
    if (!container || container.dataset.abanoAccordion) return;
    container.dataset.abanoAccordion = 'true';

    const setOpen = (item, open) => {
      const caret = item.querySelector(':scope > .abano-drawer__row > .abano-drawer__caret');
      const sub = item.querySelector(':scope > .abano-drawer__sub');
      if (!caret || !sub) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!open) item.querySelectorAll(':scope .abano-drawer__item.is-open').forEach((nested) => setOpen(nested, false));
      item.classList.toggle('is-open', open);
      const titleLink = item.querySelector(':scope > .abano-drawer__row > .abano-drawer__link[aria-expanded]');
      if (titleLink) titleLink.setAttribute('aria-expanded', open ? 'true' : 'false');
      caret.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (reduced) { sub.hidden = !open; sub.style.maxHeight = ''; return; }
      const done = () => { if (item.classList.contains('is-open')) sub.style.maxHeight = 'none'; else { sub.hidden = true; sub.style.maxHeight = ''; } };
      const onEnd = (e) => { if (e.target === sub && e.propertyName === 'max-height') { sub.removeEventListener('transitionend', onEnd); done(); } };
      sub.removeEventListener('transitionend', sub._abanoEnd || onEnd);
      sub._abanoEnd = onEnd;
      if (open) {
        sub.hidden = false;
        sub.style.maxHeight = '0px';
        void sub.offsetHeight; // reflow so the transition starts from 0
        sub.style.maxHeight = sub.scrollHeight + 'px';
      } else {
        sub.style.maxHeight = sub.scrollHeight + 'px';
        void sub.offsetHeight;
        sub.style.maxHeight = '0px';
      }
      sub.addEventListener('transitionend', onEnd);
      setTimeout(() => { if (sub._abanoEnd === onEnd) { sub.removeEventListener('transitionend', onEnd); done(); } }, 400);
    };

    const toggle = (caret) => {
      const item = caret.closest('.abano-drawer__item');
      setOpen(item, !item.classList.contains('is-open'));
    };

    container.querySelectorAll('.abano-drawer__caret').forEach((caret) => {
      caret.addEventListener('click', (e) => { e.preventDefault(); toggle(caret); });
      caret.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(caret); }
      });
    });
    // the title of an item with a submenu toggles it too (the submenu starts with a "Tutto …" link to the parent page)
    container.querySelectorAll('.abano-drawer__item.has-children > .abano-drawer__row > .abano-drawer__link').forEach((link) => {
      const caret = link.parentElement.querySelector(':scope > .abano-drawer__caret');
      if (!caret) return;
      link.setAttribute('aria-expanded', 'false');
      link.setAttribute('aria-controls', caret.getAttribute('aria-controls'));
      link.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        toggle(caret);
        link.setAttribute('aria-expanded', caret.getAttribute('aria-expanded'));
      });
    });

    // Scroll lock: fix the body at the current scroll offset while the drawer is open (works on iOS Safari,
    // unlike overflow:hidden alone) and restore the offset on close.
    let locked = false;
    let lockY = 0;
    const sync = () => {
      const open = container.classList.contains('menu-opening');
      if (open && !locked) {
        locked = true;
        lockY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = -lockY + 'px';
        document.documentElement.classList.add('abano-drawer-open');
        document.body.classList.add('abano-drawer-open');
      } else if (!open && locked) {
        locked = false;
        document.body.classList.remove('abano-drawer-open');
        document.documentElement.classList.remove('abano-drawer-open');
        document.body.style.top = '';
        window.scrollTo({ top: lockY, left: 0, behavior: 'instant' });
      }
      if (!open) container.querySelectorAll('.abano-drawer__item.is-open').forEach((item) => setOpen(item, false));
    };
    new MutationObserver(sync).observe(container, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  /* ---------- Cookie banner ---------- */
  function initCookieBanner() {
    const banner = document.getElementById('abano-cookie');
    if (!banner) return;
    const KEY = 'abano_cookie_consent';
    let stored = null;
    try { stored = localStorage.getItem(KEY); } catch (e) { /* ignore */ }
    if (!stored) banner.hidden = false;

    const applyConsent = (consent) => {
      try { localStorage.setItem(KEY, JSON.stringify(consent)); } catch (e) { /* ignore */ }
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
        try {
          window.Shopify.customerPrivacy.setTrackingConsent(
            { analytics: !!consent.statistics, marketing: !!consent.marketing, preferences: !!consent.preferences, sale_of_data: !!consent.marketing },
            () => {}
          );
        } catch (e) { /* ignore */ }
      }
      banner.hidden = true;
    };

    banner.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cookie-action]');
      if (!btn) return;
      const action = btn.dataset.cookieAction;
      const prefs = banner.querySelector('.abano-cookie__prefs');
      if (action === 'accept') applyConsent({ preferences: true, statistics: true, marketing: true });
      else if (action === 'deny') applyConsent({ preferences: false, statistics: false, marketing: false });
      else if (action === 'preferences') { banner.hidden = false; prefs.hidden = !prefs.hidden; }
      else if (action === 'save') {
        applyConsent({
          preferences: prefs.querySelector('[name="preferences"]').checked,
          statistics: prefs.querySelector('[name="statistics"]').checked,
          marketing: prefs.querySelector('[name="marketing"]').checked,
        });
      }
    });

    document.querySelectorAll('[data-abano-cookie-settings]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        banner.hidden = false;
        banner.querySelector('.abano-cookie__prefs').hidden = false;
      });
    });
  }

  /* ---------- Exit popup (discount for visitors leaving with items in the cart) ---------- */
  function initExitPopup() {
    const root = document.getElementById('abano-exit');
    if (!root || root.dataset.abanoBound) return;
    root.dataset.abanoBound = '1';
    const dialog = root.querySelector('.abano-exit__dialog');
    const KEY = 'abano_exit_seen';
    const wait = (parseInt(root.dataset.days, 10) || 7) * 864e5;
    const loadedAt = Date.now();
    let count = parseInt(root.dataset.cartCount, 10) || 0;
    let isOpen = false;
    let opener = null;

    const suppressed = () => {
      try { return Date.now() - Number(localStorage.getItem(KEY) || 0) < wait; } catch (e) { return false; }
    };
    const cartCount = () => {
      const badge = document.querySelector('#cart-icon-bubble .abano-cart-count > span[aria-hidden]');
      return badge ? parseInt(badge.textContent, 10) || count : count;
    };
    const canShow = () => !isOpen && !suppressed() && cartCount() > 0 && Date.now() - loadedAt > 3000;

    const show = () => {
      if (!canShow()) return false;
      isOpen = true;
      try { localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* ignore */ }
      opener = document.activeElement;
      root.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-open')));
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function') trapFocus(root, dialog);
      return true;
    };
    const hide = () => {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('is-open');
      root.hidden = true;
      /* the cart drawer keeps its own scroll lock while it is open */
      if (!document.querySelector('cart-drawer.active')) document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(opener && opener !== document.body ? opener : null);
    };

    root.addEventListener('click', (e) => {
      if (e.target === root) { hide(); return; }
      const btn = e.target.closest('[data-exit-action]');
      if (!btn) return;
      if (btn.dataset.exitAction === 'close') { hide(); return; }
      const label = btn.querySelector('[data-exit-copy-label]');
      const done = () => {
        if (!label) return;
        const text = label.dataset.text || label.textContent;
        label.dataset.text = text;
        label.textContent = 'Copiato ✓';
        setTimeout(() => { label.textContent = text; }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(btn.dataset.code).then(done).catch(() => {});
    });
    document.addEventListener('keyup', (e) => { if (e.key === 'Escape') hide(); });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const n = event && event.cartData && event.cartData.item_count;
        if (typeof n === 'number') count = n;
      });
    }

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      /* desktop: pointer leaves through the top of the viewport (tab bar, address bar, close button) */
      document.documentElement.addEventListener('mouseleave', (e) => { if (e.clientY <= 0) show(); });
      return;
    }

    /* touch: coming back to the tab after switching away… */
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') show(); });

    /* …and the back button, caught with one guard history entry. Browsers ignore entries pushed without a user
       gesture, so it is armed on the first tap once the cart has items. */
    let armed = !!(history.state && history.state.abanoExit);
    document.addEventListener('pointerdown', () => {
      if (armed || suppressed() || cartCount() < 1) return;
      armed = true;
      try { history.pushState({ abanoExit: 1 }, ''); } catch (e) { /* ignore */ }
    });
    window.addEventListener('popstate', () => {
      if (!armed) return;
      armed = false;
      if (!show()) history.back();
    });
  }

  /* ---------- Wishlist / Compare (localStorage) ---------- */
  const Lists = {
    key: (name) => 'abano_' + name,
    get(name) {
      try { return JSON.parse(localStorage.getItem(this.key(name)) || '[]'); } catch (e) { return []; }
    },
    set(name, arr) {
      try { localStorage.setItem(this.key(name), JSON.stringify(arr)); } catch (e) { /* ignore */ }
      document.dispatchEvent(new CustomEvent('abano:list-change', { detail: { name, items: arr } }));
    },
    toggle(name, handle) {
      const arr = this.get(name);
      const i = arr.indexOf(handle);
      if (i === -1) arr.push(handle); else arr.splice(i, 1);
      this.set(name, arr);
      return i === -1;
    },
    has(name, handle) { return this.get(name).includes(handle); },
  };
  window.AbanoLists = Lists;

  function syncListButtons() {
    document.querySelectorAll('[data-abano-list]').forEach((btn) => {
      const on = Lists.has(btn.dataset.abanoList, btn.dataset.handle);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = btn.querySelector('.abano-list-btn__label');
      if (label) label.textContent = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
    });
  }

  function initListButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-abano-list]');
      if (!btn) return;
      e.preventDefault();
      Lists.toggle(btn.dataset.abanoList, btn.dataset.handle);
      syncListButtons();
    });
    document.addEventListener('abano:list-change', syncListButtons);
    syncListButtons();
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat('it-IT', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
    } catch (e) {
      return '€' + (cents / 100).toFixed(2);
    }
  }

  async function fetchProduct(handle) {
    const res = await fetch(`${window.shopUrl || ''}/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(handle);
    return res.json();
  }

  function initListPages() {
    document.querySelectorAll('[data-abano-list-page]').forEach(async (root) => {
      const name = root.dataset.abanoListPage;
      const content = root.querySelector('[data-abano-list-content]');
      const empty = root.querySelector('[data-abano-list-empty]');
      const tpl = document.querySelector('[data-abano-list-card]');
      const currency = root.dataset.currency || 'EUR';

      const render = async () => {
        const handles = Lists.get(name);
        content.querySelectorAll('.abano-list-item, .abano-compare-table').forEach((n) => n.remove());
        content.classList.toggle('abano-list-page__content--compare', name === 'compare');
        if (!handles.length) { empty.hidden = false; return; }
        empty.hidden = true;
        const products = (await Promise.allSettled(handles.map(fetchProduct))).filter((r) => r.status === 'fulfilled').map((r) => r.value);

        if (name === 'compare') {
          const rows = [
            ['', (p) => `<a href="/products/${p.handle}"><img src="${(p.featured_image || '')}&width=280" alt="${p.title}" loading="lazy"></a>`],
            ['Prodotto', (p) => `<a href="/products/${p.handle}">${p.title}</a>`],
            ['Prezzo', (p) => formatMoney(p.price, currency)],
            ['Descrizione', (p) => `<div style="text-align:left;font-size:12px">${(p.description || '').replace(/<[^>]+>/g, ' ').slice(0, 300)}…</div>`],
            ['Disponibilità', (p) => (p.available ? 'Disponibile' : 'Esaurito')],
            ['', (p) => `<a class="abano-btn abano-btn--dark abano-btn--small" href="/products/${p.handle}">Aggiungi al carrello</a><br><button type="button" class="abano-list-item__remove" data-abano-remove="${p.handle}" style="margin-top:8px">Rimuovi</button>`],
          ];
          const table = document.createElement('table');
          table.className = 'abano-compare-table';
          table.innerHTML = rows.map(([label, fn]) => `<tr><th>${label}</th>${products.map((p) => `<td>${fn(p)}</td>`).join('')}</tr>`).join('');
          content.appendChild(table);
        } else {
          products.forEach((p) => {
            const html = tpl.innerHTML
              .replace(/\{url\}/g, `/products/${p.handle}`)
              .replace(/\{image\}/g, p.featured_image ? p.featured_image + '&width=533' : '')
              .replace(/\{title\}/g, p.title.replace(/"/g, '&quot;'))
              .replace(/\{price\}/g, formatMoney(p.price, currency))
              .replace(/\{handle\}/g, p.handle);
            content.insertAdjacentHTML('beforeend', html);
          });
        }
      };

      content.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-abano-remove]');
        if (!btn) return;
        Lists.toggle(name, btn.dataset.abanoRemove);
        render();
      });
      render();
    });
  }

  /* ---------- Product page: [quantity] [add to cart] [wishlist heart] on one row ----------
     Dawn's product-info.js only swaps the innerHTML of #price-/#Sku-/#Inventory-/#Quantity-Form-
     on variant change, so the moved wrappers survive; the whole <product-info> is re-created on
     combined-listing swaps and in the quick-add modal, both of which dispatch 'product-info:loaded'. */
  function initBuyRow(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.product__info-container .product-form__buttons').forEach((buttons) => {
      const info = buttons.closest('.product__info-container');
      if (!info) return;
      const qty = info.querySelector('.product-form__quantity');
      if (qty && !qty.dataset.abanoMoved) { buttons.prepend(qty); qty.dataset.abanoMoved = '1'; }
      const wishlist = info.querySelector('.product__wishlist');
      if (wishlist && !wishlist.dataset.abanoMoved) {
        const submit = buttons.querySelector('.product-form__submit');
        if (submit) submit.after(wishlist); else buttons.appendChild(wishlist);
        wishlist.dataset.abanoMoved = '1';
      }
      buttons.classList.add('abano-buy-row');
    });
  }
  document.addEventListener('product-info:loaded', (e) => initBuyRow(e.target));

  /* keep the active thumbnail in view in the vertical rail */
  function initGalleryRail() {
    document.querySelectorAll('.abano-pdp .thumbnail-list').forEach((list) => {
      if (list.dataset.abanoRail) return;
      list.dataset.abanoRail = '1';
      new MutationObserver((records) => {
        records.forEach((r) => {
          if (r.target.getAttribute('aria-current') === 'true') r.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        });
      }).observe(list, { attributes: true, attributeFilter: ['aria-current'], subtree: true });
    });
  }

  /* ---------- Collection toolbar selects ---------- */
  function initToolbar() {
    document.querySelectorAll('[data-abano-navigate]').forEach((sel) => {
      sel.addEventListener('change', () => { if (sel.value) window.location.href = sel.value; });
    });
    document.querySelectorAll('[data-abano-submit]').forEach((sel) => {
      sel.addEventListener('change', () => sel.form && sel.form.submit());
    });
  }

  /* ---------- Video modal: play on open, pause on close ---------- */
  function initVideoModals() {
    document.querySelectorAll('.abano-video-modal').forEach((modal) => {
      const observer = new MutationObserver(() => {
        const video = modal.querySelector('video');
        if (!video) return;
        if (modal.hasAttribute('open')) video.play().catch(() => {});
        else video.pause();
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  /* reveal-on-scroll for [data-abano-reveal] (CSS in abano.css); no-ops without IntersectionObserver or with reduced motion */
  function initReveal() {
    const items = document.querySelectorAll('[data-abano-reveal]:not(.is-visible)');
    if (!items.length) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) { items.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-visible'); else io.observe(el); });
  }

  /* keep secondary cart badges (sticky nav tools, drawer) in sync with #cart-icon-bubble, which Dawn re-renders on every cart change */
  function initCartMirror() {
    const src = document.getElementById('cart-icon-bubble');
    if (!src || src.dataset.abanoMirror) return;
    src.dataset.abanoMirror = '1';
    const sync = () => {
      const count = src.querySelector('.abano-cart-count > span[aria-hidden]')?.textContent.trim() || '';
      document.querySelectorAll('[data-abano-cart-mirror]').forEach((el) => {
        let badge = el.querySelector('.abano-cart-count');
        if (!count) { badge?.remove(); return; }
        if (!badge) { badge = document.createElement('span'); badge.className = 'abano-cart-count'; el.appendChild(badge); }
        badge.textContent = count;
      });
    };
    new MutationObserver(sync).observe(src, { childList: true, subtree: true, characterData: true });
  }

  /* Dawn's CartDrawer only binds #cart-icon-bubble, which here is the visually-hidden label inside the header cart
     links, so open the drawer from every cart icon / button (header, sticky nav tools, mobile menu footer). */
  function initCartDrawerTriggers() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    document.querySelectorAll('a.abano-header-icon--cart, .header__nav-tools a[data-abano-cart-mirror], a.abano-drawer__cart').forEach((link) => {
      if (link.dataset.abanoCartBound) return;
      link.dataset.abanoCartBound = 'true';
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || typeof drawer.open !== 'function') return;
        event.preventDefault();
        const menu = document.querySelector('#Details-menu-drawer-container[open] > summary');
        if (menu) menu.click();
        drawer.open(link);
      });
    });
  }

  function init() {
    initReveal();
    initCartMirror();
    initCartDrawerTriggers();
    initHoverMenus();
    initDrawerClose();
    initDrawerAccordion();
    initCookieBanner();
    initExitPopup();
    initListButtons();
    initListPages();
    initToolbar();
    initVideoModals();
    initBuyRow();
    initGalleryRail();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => { initReveal(); initHoverMenus(); initDrawerClose(); initDrawerAccordion(); syncListButtons(); initToolbar(); initBuyRow(); initGalleryRail(); });
})();
